import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function QCGuidePage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://oopbuyspread.quest';
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main>
      <nav className="fixed top-0 left-0 w-full z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-pie-chart-line text-background-50 text-base"></i>
            </div>
            <span className="font-heading text-foreground-900 text-lg font-semibold whitespace-nowrap">
              OOPBUY Spreadsheet
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-foreground-600 text-sm font-medium hover:text-foreground-900 transition-colors whitespace-nowrap">
              Startseite
            </Link>
            <Link to="/artikel/oopbuy-spreadsheet-einkaufsguide" className="text-foreground-600 text-sm font-medium hover:text-foreground-900 transition-colors whitespace-nowrap">
              Einkaufsguide
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Buy it now
            </a>
          </div>
        </div>
      </nav>

      <article className="pt-24 pb-16">
        <div className="max-w-[800px] mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-500 transition-colors">
              <i className="ri-home-4-line"></i>
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to="/" className="hover:text-primary-500 transition-colors">Startseite</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-600">OOPBUY Spreadsheet QC Guide</span>
          </nav>

          <header className="mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-5">
              <i className="ri-shield-check-line text-xs"></i>
              <span>Qualitätskontrolle</span>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-900 font-bold leading-tight">
              OOPBUY Spreadsheet QC Guide: So funktioniert die Qualitätskontrolle
            </h1>
            <p className="mt-4 text-foreground-500 text-base md:text-lg leading-relaxed">
              Erfahren Sie alles über die Qualitätskontrolle im OOPBUY Spreadsheet. Von QC-Fotos über Größenvergleiche bis hin zur Bewertung von Material und Verarbeitung.
            </p>
            <div className="flex items-center gap-4 mt-6 text-foreground-400 text-sm">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                Aktualisiert: Juli 2026
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                7 Min. Lesezeit
              </span>
            </div>
          </header>

          <div className="relative rounded-2xl overflow-hidden mb-12">
            <img
              src="https://readdy.ai/api/search-image?query=Close%20up%20macro%20shot%20of%20hands%20carefully%20inspecting%20a%20white%20designer%20sneaker%20on%20a%20clean%20minimal%20desk%20surface%2C%20a%20magnifying%20glass%20and%20checklist%20notebook%20with%20green%20checkmarks%20beside%20it%2C%20soft%20natural%20daylight%20from%20a%20large%20window%2C%20quality%20inspection%20theme%2C%20professional%20product%20review%20aesthetic%2C%20warm%20cream%20and%20beige%20palette%2C%20cotton%20and%20leather%20textures%20visible%2C%20clean%20modern%20workspace%2C%20editorial%20photography%20style&width=1200&height=600&seq=qc-guide-hero-01&orientation=landscape"
              alt="OOPBUY Spreadsheet QC Guide"
              className="w-full aspect-video object-cover"
            />
          </div>

          <div className="text-foreground-700 leading-relaxed space-y-10">
            <section>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 font-bold mb-5">
                Was bedeutet QC im OOPBUY Spreadsheet?
              </h2>
              <p className="text-foreground-600 leading-relaxed">
                QC steht für <strong>Quality Control</strong> – also Qualitätskontrolle. Im <strong>OOPBUY Spreadsheet</strong> ist die QC einer der wichtigsten Bestandteile. Bevor ein Produkt in das Spreadsheet aufgenommen wird, durchläuft es einen gründlichen Prüfprozess. Erfahrene Community-Mitglieder bestellen die Produkte selbst, machen detaillierte Fotos und bewerten sie nach standardisierten Kriterien.
              </p>
              <p className="mt-4 text-foreground-600 leading-relaxed">
                Anders als bei vielen anderen Spreadsheets werden hier keine blinden Empfehlungen ausgesprochen. Jeder Eintrag im <strong>OOPBUY Spreadsheet</strong> basiert auf echten Käufen und praktischen Tests. Das gibt Ihnen als Käufer die Sicherheit, dass Sie keine bösen Überraschungen erleben – ein riesiger Vorteil besonders für Einsteiger in den China-Einkauf.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 font-bold mb-5">
                Die 5 QC-Bewertungskriterien im Detail
              </h2>
              <p className="text-foreground-600 leading-relaxed">
                Jedes Produkt im <strong>OOPBUY Spreadsheet</strong> wird anhand von fünf zentralen Kriterien bewertet. Diese standardisierte Bewertung macht die Qualität verschiedener Produkte direkt vergleichbar:
              </p>

              <div className="mt-6 space-y-4">
                {[
                  { num: '01', title: 'Materialqualität', desc: 'Die verwendeten Stoffe, Lederqualitäten und Textilien werden genau unter die Lupe genommen. Echtes Leder oder Kunstleder? Baumwolle oder Polyester? Das OOPBUY Spreadsheet gibt klare Auskunft über die tatsächliche Materialzusammensetzung – oft mit Vergleichsfotos zum Originalprodukt.', icon: 'ri-scissors-line' },
                  { num: '02', title: 'Passform & Sizing', desc: 'Einer der kritischsten Punkte beim Online-Einkauf. Das Spreadsheet enthält detaillierte Größenangaben und Empfehlungen, ob ein Produkt größengetreu ausfällt oder ob Sie eine Nummer größer oder kleiner bestellen sollten. Inklusive Maßtabellen und Vergleich mit EU-Größen.', icon: 'ri-ruler-line' },
                  { num: '03', title: 'Farbgenauigkeit', desc: 'Produktfotos können täuschen. Die QC im OOPBUY Spreadsheet vergleicht die tatsächliche Farbe mit den Shop-Bildern und dokumentiert Abweichungen. Besonders bei Sneakern und Kleidung ist dieser Punkt entscheidend für die Kaufentscheidung.', icon: 'ri-palette-line' },
                  { num: '04', title: 'Verarbeitung & Details', desc: 'Nähte, Drucke, Stickereien, Logos – alles wird auf Herz und Nieren geprüft. Gibt es lose Fäden? Ist das Logo sauber gestickt? Sind die Drucke scharf und haltbar? Das OOPBUY Spreadsheet dokumentiert jedes Detail mit Nahaufnahmen.', icon: 'ri-zoom-in-line' },
                  { num: '05', title: 'Verpackung & Zustand', desc: 'Wie kommt das Produkt bei Ihnen an? Die QC bewertet auch die Versandverpackung und den Zustand nach dem Transport. Gibt es Druckstellen? Ist die Originalverpackung intakt? Diese Informationen helfen, Enttäuschungen beim Auspacken zu vermeiden.', icon: 'ri-box-3-line' },
                ].map((item) => (
                  <div key={item.num} className="flex items-start gap-4 p-5 rounded-2xl bg-background-100 border border-background-200/70">
                    <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-accent-600 text-lg`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-xs font-bold text-accent-500">{item.num}</span>
                        <h3 className="text-foreground-900 font-semibold text-sm">{item.title}</h3>
                      </div>
                      <p className="text-foreground-600 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 font-bold mb-5">
                So lesen Sie QC-Fotos richtig
              </h2>
              <p className="text-foreground-600 leading-relaxed">
                Die QC-Fotos im <strong>OOPBUY Spreadsheet</strong> sind Ihr wichtigstes Werkzeug. Aber worauf sollten Sie achten? Hier die wichtigsten Tipps:
              </p>
              <ul className="mt-4 space-y-3">
                {[
                  'Vergleichen Sie die QC-Fotos immer mit den Original-Produktfotos aus dem Shop',
                  'Achten Sie auf die Beleuchtung – manche Farbabweichungen entstehen durch schlechtes Licht im QC-Lager',
                  'Prüfen Sie die Detailaufnahmen von Logos, Stickereien und Nähten besonders genau',
                  'Messen Sie die angegebenen Maße mit Ihrer eigenen Kleidung nach',
                  'Lesen Sie die schriftlichen QC-Notizen – sie enthalten oft wichtige Zusatzinformationen',
                ].map((tip, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-primary-600 text-xs"></i>
                    </div>
                    <span className="text-foreground-600 text-sm">{tip}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 font-bold mb-5">
                QC-Bewertungsskala im OOPBUY Spreadsheet
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-background-200">
                      <th className="text-left py-3 px-4 text-foreground-900 font-semibold">Bewertung</th>
                      <th className="text-left py-3 px-4 text-foreground-900 font-semibold">Bedeutung</th>
                      <th className="text-left py-3 px-4 text-foreground-900 font-semibold">Empfehlung</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['⭐⭐⭐⭐⭐', 'Hervorragende Qualität, nahezu identisch mit dem Original', 'Uneingeschränkt empfehlenswert'],
                      ['⭐⭐⭐⭐', 'Sehr gute Qualität mit minimalen Abweichungen', 'Empfehlenswert'],
                      ['⭐⭐⭐', 'Gute Qualität, kleine Mängel möglich', 'Mit Vorbehalt empfehlenswert'],
                      ['⭐⭐', 'Mittelmäßige Qualität, deutliche Abweichungen', 'Nur bedingt empfehlenswert'],
                      ['⭐', 'Mangelhafte Qualität', 'Nicht empfehlenswert'],
                    ].map(([rating, meaning, recommendation]) => (
                      <tr key={rating} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                        <td className="py-3 px-4 text-foreground-800 font-medium whitespace-nowrap">{rating}</td>
                        <td className="py-3 px-4 text-foreground-600">{meaning}</td>
                        <td className="py-3 px-4 text-foreground-500">{recommendation}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="p-6 md:p-8 rounded-2xl bg-accent-50 border border-accent-200/50">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 font-bold mb-3">
                Fazit: QC ist Ihr bester Freund beim China-Einkauf
              </h2>
              <p className="text-foreground-600 leading-relaxed">
                Die Qualitätskontrolle im <strong>OOPBUY Spreadsheet</strong> ist das Herzstück des gesamten Systems. Ohne QC wären Sie auf die oft irreführenden Shop-Fotos angewiesen. Mit den detaillierten QC-Bewertungen und Fotos treffen Sie fundierte Kaufentscheidungen und vermeiden teure Fehlkäufe. Nehmen Sie sich die Zeit, die QC-Informationen gründlich zu studieren – es lohnt sich bei jeder einzelnen Bestellung.
              </p>
            </section>
          </div>

          <div className="mt-12 p-6 rounded-2xl bg-background-100 border border-background-200/70">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
              <div>
                <h3 className="text-foreground-900 font-semibold text-lg">Jetzt das OOPBUY Spreadsheet nutzen</h3>
                <p className="text-foreground-500 text-sm mt-1">Entdecken Sie alle geprüften Produkte auf kaspreadsheet.com</p>
              </div>
              <div className="flex items-center gap-3">
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap">
                  OOPBUY Spreadsheet
                </a>
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-6 py-2.5 bg-foreground-900 text-background-50 text-sm font-semibold rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap">
                  Buy it now
                </a>
              </div>
            </div>
          </div>

          <nav className="mt-10 pt-8 border-t border-background-200/70 flex flex-wrap items-center justify-between gap-4">
            <Link to="/" className="inline-flex items-center gap-2 text-foreground-600 hover:text-primary-500 transition-colors text-sm font-medium">
              <i className="ri-arrow-left-line"></i>
              Zurück zur Startseite
            </Link>
            <Link to="/artikel/oopbuy-spreadsheet-guide" className="inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 transition-colors text-sm font-medium">
              Hauptguide lesen
              <i className="ri-arrow-right-line"></i>
            </Link>
          </nav>
        </div>
      </article>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'OOPBUY Spreadsheet QC Guide: So funktioniert die Qualit&auml;tskontrolle',
        description: 'Erfahren Sie alles &uuml;ber die Qualit&auml;tskontrolle im OOPBUY Spreadsheet. Von QC-Fotos &uuml;ber Gr&ouml;&szlig;envergleiche bis hin zur Bewertung von Material und Verarbeitung.',
        author: { '@type': 'Organization', name: 'OOPBUY Spreadsheet Guide' },
        publisher: { '@type': 'Organization', name: 'OOPBUY Spreadsheet Guide', logo: { '@type': 'ImageObject', url: siteUrl + '/favicon.ico' } },
        datePublished: '2026-07-01',
        dateModified: '2026-07-15',
        mainEntityOfPage: { '@type': 'WebPage', '@id': siteUrl + '/artikel/oopbuy-spreadsheet-qc-guide' },
        inLanguage: 'de',
      }) }} />

      <footer className="relative bg-foreground-900 mx-4 md:mx-6 mb-4 md:mb-6 rounded-3xl overflow-hidden">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-pie-chart-line text-background-50 text-base"></i>
              </div>
              <span className="font-heading text-background-50 text-base font-semibold">OOPBUY Spreadsheet</span>
            </Link>
            <p className="text-foreground-500 text-xs">Unabhängige Informationsressource – nicht offiziell mit OOPBUY verbunden.</p>
          </div>
        </div>
      </footer>
    </main>
  );
}